import { Component, ElementRef, HostBinding, HostListener, ViewChild } from '@angular/core'
import { Router } from '@angular/router'
import { faBell, faCheckDouble, faXmark } from '@fortawesome/free-solid-svg-icons'
import { NotificationsService, UserNotification } from './notifications.service'

const BELL_LEFT_STORAGE_KEY = 'fs.notifications-bell.left'
const BELL_TOP_STORAGE_KEY = 'fs.notifications-bell.top'

@Component({
  selector: 'fs-notifications-bell',
  templateUrl: './notifications-bell.component.html',
  styleUrls: ['./notifications-bell.component.scss'],
})
export class NotificationsBellComponent {
  private readonly dragThreshold = 6
  private readonly bellSize = 56
  private readonly viewportPadding = 12
  readonly state$ = this.notifications.state$
  private clickTimeoutId?: ReturnType<typeof setTimeout>
  private _panelRoot?: ElementRef<HTMLElement>
  @ViewChild('panelRoot')
  get panelRoot(): ElementRef<HTMLElement> | undefined {
    return this._panelRoot
  }
  set panelRoot(ref: ElementRef<HTMLElement> | undefined) {
    this._panelRoot = ref
    this.updatePanelPosition()
  }
  @ViewChild('bellButton') bellButton?: ElementRef<HTMLElement>

  readonly faBell = faBell
  readonly faTimes = faXmark
  readonly faCheckDouble = faCheckDouble

  isDragging = false
  ignoreClick = false
  bellLeft?: number
  bellTop?: number

  constructor(private readonly notifications: NotificationsService, private readonly router: Router) {
    this.notifications.initializeBellDocking()
    this.restorePosition()
    this.notifications.state$.subscribe(() => {
      this.updatePanelPosition()
    })
  }

  private updatePanelPosition() {
    const el = this._panelRoot?.nativeElement
    if (!el) return
    const state = this.notifications.stateSnapshot
    if (state.panelAnchor === 'menu') {
      el.style.top = state.panelTop != null ? `${state.panelTop}px` : ''
      el.style.bottom = state.panelBottom != null ? `${state.panelBottom}px` : ''
      el.style.left = state.panelLeft != null ? `${state.panelLeft}px` : ''
      el.style.right = state.panelRight != null ? `${state.panelRight}px` : ''
      el.style.maxHeight = state.panelMaxHeight != null ? `${state.panelMaxHeight}px` : ''
    } else {
      el.style.top = ''
      el.style.bottom = ''
      el.style.left = ''
      el.style.right = ''
      el.style.maxHeight = ''
    }
  }

  get isDockedToUserMenu() {
    return this.notifications.isBellDockedToUserMenu
  }

  @HostBinding('style.left.px')
  get hostLeft(): number | null {
    return typeof this.bellLeft === 'number' ? this.bellLeft : null
  }

  @HostBinding('style.top.px')
  get hostTop(): number | null {
    return typeof this.bellTop === 'number' ? this.bellTop : null
  }

  @HostBinding('style.right')
  get hostRight(): string {
    return this.hasCustomPosition ? 'auto' : '1.5rem'
  }

  @HostBinding('style.bottom')
  get hostBottom(): string {
    return this.hasCustomPosition ? 'auto' : '1.5rem'
  }

  get hasCustomPosition() {
    return typeof this.bellLeft === 'number' && typeof this.bellTop === 'number'
  }

  togglePanel() {
    if (this.ignoreClick) {
      this.ignoreClick = false
      return
    }

    this.notifications.toggleBellPanel()
  }

  handleBellClick(event: MouseEvent) {
    if (event.detail > 1) {
      return
    }

    if (this.clickTimeoutId) {
      clearTimeout(this.clickTimeoutId)
    }

    this.clickTimeoutId = setTimeout(() => {
      this.clickTimeoutId = undefined
      this.togglePanel()
    }, 220)
  }

  handleBellDoubleClick(event: MouseEvent) {
    event.preventDefault()
    event.stopPropagation()

    if (this.clickTimeoutId) {
      clearTimeout(this.clickTimeoutId)
      this.clickTimeoutId = undefined
    }

    this.closePanel()
  }

  closePanel() {
    this.notifications.closePanel()
  }

  markAsRead(item: UserNotification) {
    this.notifications.markAsRead(item.id)
  }

  markAllAsRead() {
    this.notifications.markAllAsRead()
  }

  getMetadata(item: UserNotification) {
    return (item.metadata ?? {}) as {
      imageUrl?: string
      downloadUrl?: string
      id_material?: string
      productName?: string
      supplierNames?: string[]
    }
  }

  getImageUrl(item: UserNotification) {
    const metadata = this.getMetadata(item)
    return metadata.imageUrl || metadata.downloadUrl || null
  }

  getProductLabel(item: UserNotification) {
    const metadata = this.getMetadata(item)
    if (!metadata.id_material) {
      return null
    }

    return metadata.productName ? `${metadata.id_material} - ${metadata.productName}` : metadata.id_material
  }

  getSupplierLabel(item: UserNotification) {
    const supplierNames = this.getMetadata(item).supplierNames ?? []
    const validSupplierNames = supplierNames.filter(supplierName => `${supplierName}`.trim().length > 0)
    return validSupplierNames.length ? validSupplierNames.join(', ') : null
  }

  openImage(item: UserNotification) {
    const imageUrl = this.getImageUrl(item)
    if (!imageUrl) {
      return
    }

    window.open(imageUrl, '_blank', 'noopener')
  }

  downloadImage(item: UserNotification) {
    const imageUrl = this.getImageUrl(item)
    if (!imageUrl) {
      return
    }

    const link = document.createElement('a')
    link.href = imageUrl
    link.download = ''
    link.target = '_blank'
    link.rel = 'noopener'
    link.click()
  }

  openLink(item: UserNotification) {
    if (!item.link) {
      return
    }

    void this.router.navigateByUrl(item.link)
    if (!item.readAt) {
      this.notifications.markAsRead(item.id)
    }
    this.notifications.closePanel()
  }

  trackById(_: number, item: UserNotification) {
    return item.id
  }

  startDrag(event: MouseEvent) {
    if (window.innerWidth <= 1024) {
      return
    }

    const target = event.currentTarget as HTMLElement | null
    if (!target) {
      return
    }

    event.preventDefault()

    const rect = target.getBoundingClientRect()
    const startX = event.clientX
    const startY = event.clientY
    const initialLeft = this.clampLeft(rect.left, rect.width)
    const initialTop = this.clampTop(rect.top, rect.height)
    let hasDragged = false

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX
      const deltaY = moveEvent.clientY - startY

      if (!hasDragged && Math.hypot(deltaX, deltaY) < this.dragThreshold) {
        return
      }

      if (!hasDragged) {
        hasDragged = true
        this.isDragging = true
        this.ignoreClick = true
      }

      this.bellLeft = this.clampLeft(initialLeft + deltaX, rect.width)
      this.bellTop = this.clampTop(initialTop + deltaY, rect.height)
    }

    const handleMouseUp = () => {
      this.isDragging = false
      if (hasDragged) {
        this.notifications.setBellDockedToUserMenu(this.shouldDockToUserMenu(rect.width, rect.height))
      }
      this.persistPosition()
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
  }

  @HostListener('window:resize')
  handleResize() {
    const state = this.notifications.stateSnapshot
    if (state.panelOpen && state.panelAnchor === 'menu') {
      this.notifications.closePanel()
    }

    if (!this.hasCustomPosition) {
      return
    }

    if (this.isOutsideViewport(this.bellLeft!, this.bellTop!, this.bellSize, this.bellSize)) {
      this.moveBellToVisibleStart(this.bellTop)
      this.notifications.setBellDockedToUserMenu(false)
    } else {
      this.bellLeft = this.clampLeft(this.bellLeft!, this.bellSize)
      this.bellTop = this.clampTop(this.bellTop!, this.bellSize)
    }

    this.persistPosition()
  }

  @HostListener('document:mousedown', ['$event'])
  handleDocumentMouseDown(event: MouseEvent) {
    const state = this.notifications.stateSnapshot
    if (!state.panelOpen) {
      return
    }

    const target = event.target as Node | null
    if (!target) {
      return
    }

    if (this.panelRoot?.nativeElement.contains(target)) {
      return
    }

    if (this.bellButton?.nativeElement.contains(target)) {
      return
    }

    this.notifications.closePanel()
  }

  @HostListener('document:keydown.escape')
  handleEscapeKey() {
    if (this.notifications.stateSnapshot.panelOpen) {
      this.notifications.closePanel()
    }
  }

  private clampLeft(value: number, width: number) {
    return Math.min(Math.max(this.viewportPadding, value), Math.max(this.viewportPadding, window.innerWidth - width - this.viewportPadding))
  }

  private clampTop(value: number, height: number) {
    return Math.min(
      Math.max(this.viewportPadding, value),
      Math.max(this.viewportPadding, window.innerHeight - height - this.viewportPadding),
    )
  }

  private restorePosition() {
    const left = Number(localStorage.getItem(BELL_LEFT_STORAGE_KEY))
    const top = Number(localStorage.getItem(BELL_TOP_STORAGE_KEY))

    if (Number.isFinite(left) && Number.isFinite(top)) {
      if (this.isOutsideViewport(left, top, this.bellSize, this.bellSize)) {
        this.moveBellToVisibleStart(top)
        this.notifications.setBellDockedToUserMenu(false)
        this.persistPosition()
        return
      }

      this.bellLeft = left
      this.bellTop = top
      this.handleResize()
    }
  }

  private isOutsideViewport(left: number, top: number, width: number, height: number) {
    return (
      left < this.viewportPadding ||
      top < this.viewportPadding ||
      left + width > window.innerWidth - this.viewportPadding ||
      top + height > window.innerHeight - this.viewportPadding
    )
  }

  private moveBellToVisibleStart(preferredTop?: number) {
    this.bellLeft = this.viewportPadding
    this.bellTop = this.clampTop(typeof preferredTop === 'number' ? preferredTop : window.innerHeight - this.bellSize - 24, this.bellSize)
  }

  private persistPosition() {
    if (!this.hasCustomPosition) {
      localStorage.removeItem(BELL_LEFT_STORAGE_KEY)
      localStorage.removeItem(BELL_TOP_STORAGE_KEY)
      return
    }

    localStorage.setItem(BELL_LEFT_STORAGE_KEY, `${this.bellLeft}`)
    localStorage.setItem(BELL_TOP_STORAGE_KEY, `${this.bellTop}`)
  }

  private shouldDockToUserMenu(width: number, height: number) {
    const userNavbar = document.querySelector('.fs-user-navbar') as HTMLElement | null
    if (!userNavbar || typeof this.bellLeft !== 'number' || typeof this.bellTop !== 'number') {
      return false
    }

    const targetRect = userNavbar.getBoundingClientRect()
    const bellRect = {
      left: this.bellLeft,
      top: this.bellTop,
      right: this.bellLeft + width,
      bottom: this.bellTop + height,
    }
    const dockingPadding = 20

    return !(
      bellRect.right < targetRect.left - dockingPadding ||
      bellRect.left > targetRect.right + dockingPadding ||
      bellRect.bottom < targetRect.top - dockingPadding ||
      bellRect.top > targetRect.bottom + dockingPadding
    )
  }
}
